import { useSelector } from "react-redux";

import { GridColDef } from "@mui/x-data-grid";
import { Column } from "../../../redux/actions/data/DataDeclarations";

import { State } from "../../../redux/slices";

const TableMiniChartColumns = (t: (arg0: string) => string) => {
  const { usersTableMiniChart } = useSelector(
    (state: State) => state.MapReducer,
  );

  // get all cereals from usersTable
  const allCereals = Array.from(
    new Set(
      usersTableMiniChart?.flatMap(
        (user) => user.varietyActions?.map((variety) => variety.cereal) || [],
      ) || [],
    ),
  );

  // Create dynamic cereal name mappings
  const dynamicCerealNames = allCereals.reduce(
    (acc, cereal) => {
      const fieldName = cereal.replaceAll(/\s+/g, "").toLowerCase();
      acc[fieldName] = cereal; // use original cereal name as header
      return acc;
    },
    {} as Record<string, string>,
  );

  const columnNames = {
    username: t("Fleet_overview.table.users"),
    site: t("Dashboard.activityTable.site"),
    totalActions: t("Dashboard.number_of_actions"),
    lastActionAt: t("Dashboard.lastActionAt"),
    ...dynamicCerealNames,
  };

  // Create base columns
  const baseColumns = [
    {
      name: "id",
      valueType: "string",
      hidden: true,
      hideable: true,
      sortable: false,
      isStatic: true,
      alwaysHide: true,
    },
    {
      name: "username",
      valueType: "string",
      hidden: false,
      hideable: true,
      sortable: false,
      isStatic: true,
      alwaysHide: false,
    },
    {
      name: "totalActions",
      valueType: "string",
      hidden: false,
      hideable: true,
      sortable: true,
      isStatic: true,
      alwaysHide: false,
    },
  ];

  const cerealColumns = allCereals.map((cereal) => ({
    name: cereal.replaceAll(/\s+/g, "").toLowerCase(),
    originalName: cereal,
    valueType: "string",
    hidden: false,
    hideable: true,
    sortable: true,
    isStatic: true,
    alwaysHide: false,
  }));

  // Combine base columns with dynamic cereal columns
  const rawColumnsActivity = [...baseColumns, ...cerealColumns] as Column[];

  const columns = rawColumnsActivity.map((columns: Column) => ({
    field: columns.name,
    headerName: columns.localizedName ? columns.localizedName : columns.name,
    disableColumnMenu: true,
    show: columns.hidden === false,
    description: "",
    sorteable: columns.sortable,

    operators: columns.filterOperators ? columns.filterOperators : [],
    static: columns.isStatic,
    alwaysHide: columns.alwaysHide ? columns.alwaysHide : false,
    isAnnotation: columns.isAnnotation ? columns.isAnnotation : false,
  }));

  let columnsValues: any[] = columns.map((column: any) => {
    return {
      field: column.field,
      headerName: (columnNames as never)[column.field],
      flex: 1,

      resizable: true,
      sortable: column.sorteable,
      show: column.show,
      disableColumnMenu: true,
      filterable: false,
      alwaysHide: column.alwaysHide,
      isAnnotation: column.isAnnotation,
    } as GridColDef;
  });

  let mapResults = (usersTableMiniChart || []).map(
    (userEntry: any, index: number) => {
      // Create base object with user data
      const row: any = {
        id: index,
        username: userEntry.user,
        site: userEntry.site,
        totalActions: userEntry.totalActions,
      };

      // Add dynamic cereal counts
      for (const cereal of allCereals) {
        const fieldName = cereal.replaceAll(/\s+/g, "").toLowerCase();
        const varietyAction = userEntry.varietyActions?.find(
          (v: any) => v.cereal === cereal,
        );
        row[fieldName] = varietyAction ? varietyAction.count : 0;
      }

      return row;
    },
  );

  const columnsFilter = columnsValues?.filter(
    (columns: any) => !columns.alwaysHide,
  );

  if (!mapResults) {
    mapResults = [];
  }

  return {
    columnsValues: columnsFilter,
    mapResults,
  };
};

export default TableMiniChartColumns;
